/**
 * Shoptet addon (footer slot): global JS.
 *
 * Konfigurace: `window.YG_CONFIG` — minimalní vzor v `config/yg-config.example.js` (volitelné klíče v DOCS.md).
 *
 * Primary purpose:
 * - observe "add to cart" flow (events + monitored functions)
 * - guards before add-to-cart (DIG mix, digitální limity, …)
 * - volitelně „Katalog zboží“ v menu (`menu-catalog.js`)
 * - checkout: doprava / platba (`checkout-billing-shipping.js`), osobní údaje (`checkout-customer-details.js`)
 */

import { installCheckoutBillingShipping } from './checkout-billing-shipping.js';
import { installCheckoutCustomerDetails } from './checkout-customer-details.js';
import { installMenuCatalog } from './menu-catalog.js';

const CONFIG_KEY = 'YG_CONFIG';
const LOG_KEY = '__YG_LOG';

function getConfig() {
  const cfg = window[CONFIG_KEY];
  return cfg && typeof cfg === 'object' ? cfg : {};
}

function isDebugEnabled(cfg) {
  return cfg?.debug === true;
}

function log(cfg, ...args) {
  // Always collect logs for easier debugging/copying from console.
  try {
    if (!Array.isArray(window[LOG_KEY])) window[LOG_KEY] = [];
    window[LOG_KEY].push({ ts: Date.now(), args });
    // keep last ~300 entries
    if (window[LOG_KEY].length > 300) window[LOG_KEY].splice(0, window[LOG_KEY].length - 300);
  } catch {
    // ignore
  }

  if (!isDebugEnabled(cfg)) return;
  // eslint-disable-next-line no-console
  console.log('[YG]', ...args);
}

function formToObject(form) {
  if (!(form instanceof HTMLFormElement)) return null;
  const fd = new FormData(form);
  /** @type {Record<string, string | string[]>} */
  const out = {};
  for (const [k, v] of fd.entries()) {
    const value = String(v);
    if (out[k] === undefined) out[k] = value;
    else if (Array.isArray(out[k])) out[k].push(value);
    else out[k] = [out[k], value];
  }
  return {
    action: form.getAttribute('action'),
    method: form.getAttribute('method'),
    className: form.className,
    id: form.id || null,
    fields: out,
  };
}

function getText(el) {
  if (!el) return null;
  const t = el.textContent;
  return typeof t === 'string' ? t.trim() : null;
}

function parseMoneyText(text) {
  // Very small helper for "743 Kč" / "743 CZK" / "743,00" etc.
  if (!text || typeof text !== 'string') return null;
  const normalized = text
    .replace(/\u00a0/g, ' ')
    .replace(/[^\d,.\-]/g, ' ')
    .trim();
  const num = normalized.replace(/\s+/g, '').replace(',', '.');
  const value = Number(num);
  return Number.isFinite(value) ? value : null;
}

/**
 * SKU z DOM: karta výpisu má často `[data-micro="sku"]` uvnitř `.p`.
 * Na detailu bývá formulář mimo `data-micro="product"` → bereme `.p-detail` + meta/schema.
 */
function resolveSkuFromDom(form) {
  if (!(form instanceof HTMLElement)) return null;

  const card = form.closest('[data-micro="product"], .p, [data-testid="productItem"]');
  if (card) {
    const t = getText(card.querySelector('[data-micro="sku"]'));
    if (t) return t;
  }

  const detail =
    form.closest('.p-detail, .product-detail, #product-detail, body.type-detail') ||
    document.querySelector('.p-detail');

  if (detail) {
    const t1 = getText(detail.querySelector('[data-micro="sku"]'));
    if (t1) return t1;
    const meta = detail.querySelector('meta[itemprop="sku"]');
    const c = meta?.getAttribute('content');
    if (c && String(c).trim()) return String(c).trim();
  }

  return null;
}

/**
 * Build a "best-effort" context object from the submit call + DOM.
 * This is the input for any future "prevent add-to-cart" rules.
 */
function buildAddToCartContext({ url, form, rawArgs }) {
  const formObj = formToObject(form);
  const fields = formObj?.fields || {};

  const productId = typeof fields.productId === 'string' ? fields.productId : null;
  const priceId = typeof fields.priceId === 'string' ? fields.priceId : null;
  const amountStr = typeof fields.amount === 'string' ? fields.amount : null;
  const amount = amountStr ? Number(amountStr) : null;

  // Listing card wrapper (detail often has no data-micro=product around the form).
  const wrapper =
    form instanceof HTMLElement ? form.closest('[data-micro="product"], .p, [data-testid="productItem"]') : null;

  const sku = resolveSkuFromDom(form);
  const name = wrapper
    ? getText(wrapper.querySelector('[data-micro="name"], [data-testid="productCardName"], .name span, .name'))
    : getText(document.querySelector('.p-detail h1, .p-detail .p-detail-inner-header h1'));
  const productUrl = wrapper ? wrapper.querySelector('a[data-micro="url"], a.name, a.image')?.getAttribute('href') : null;

  const priceRoot = wrapper || (form instanceof HTMLElement ? form.closest('.p-detail') : null);
  const microPrice = priceRoot?.querySelector('[data-micro="offer"]')?.getAttribute('data-micro-price') ?? null;
  const microCurrency = priceRoot?.querySelector('[data-micro="offer"]')?.getAttribute('data-micro-price-currency') ?? null;
  const domPriceText = priceRoot
    ? getText(priceRoot.querySelector('[data-testid="productCardPrice"], .price-final strong, .price-final'))
    : null;
  const price = microPrice ? Number(microPrice) : parseMoneyText(domPriceText);
  const currency = microCurrency || null;

  const cfg = getConfig();
  const isDigital =
    typeof sku === 'string' ? skuStartsWithAnyPrefix(sku, cfg.classification?.digitalPrefixes ?? ['DIG_']) : null;

  return {
    url,
    productId,
    priceId,
    amount: Number.isFinite(amount) ? amount : null,
    sku,
    isDigital,
    name,
    productUrl,
    price: Number.isFinite(price) ? price : null,
    currency,
    // keep raw for debugging / future edge cases
    raw: {
      argTypes: rawArgs.map(a => (a === null ? 'null' : typeof a)),
    },
  };
}

function skuStartsWithAnyPrefix(sku, prefixes) {
  if (!sku || !Array.isArray(prefixes) || !prefixes.length) return false;
  const upper = sku.toUpperCase();
  return prefixes.some(p => upper.startsWith(String(p || '').toUpperCase()));
}

function safeGetShoptetDataLayer() {
  try {
    if (typeof getShoptetDataLayer === 'function') return getShoptetDataLayer();
  } catch {
    // ignore
  }
  return null;
}

/** Shoptet do `dataLayer[0].shoptet` dává řádky košíku různě pojmenovaně (`cart` vs `cartItems`). */
function getShoptetCartLines(dl) {
  if (!dl || typeof dl !== 'object') return [];
  if (Array.isArray(dl.cart)) return dl.cart;
  if (Array.isArray(dl.cartItems)) return dl.cartItems;
  if (Array.isArray(dl.items)) return dl.items;
  return [];
}

function cartItemIsDigital(item, cfg) {
  const prefixes = cfg.classification?.digitalPrefixes ?? ['DIG_'];
  const code = typeof item?.code === 'string' ? item.code.trim() : '';
  return code !== '' && skuStartsWithAnyPrefix(code, prefixes);
}

/**
 * Pro mix guard používáme jen řádky se známým `item.code` (ne prázdné).
 * Jinak se „ne-DIG řádek“ často jen kvůli chybějícímu `code` v dataLayer počítá jako fyzický → falešný konflikt.
 * @returns {'digital'|'physical'|null}
 */
function classifyCartLineForMix(item, cfg) {
  const code = typeof item?.code === 'string' ? item.code.trim() : '';
  if (!code) return null;
  if (cartItemIsDigital(item, cfg)) return 'digital';
  return 'physical';
}

/**
 * @returns {{ hasDig: boolean, hasPhys: boolean }}
 */
function summarizeClassifiedMixInCart(lines, cfg) {
  let hasDig = false;
  let hasPhys = false;
  for (const item of lines) {
    const kind = classifyCartLineForMix(item, cfg);
    if (kind === 'digital') hasDig = true;
    else if (kind === 'physical') hasPhys = true;
    if (hasDig && hasPhys) break;
  }
  return { hasDig, hasPhys };
}

/**
 * Košík obsahuje současně klasifikovatelný digitální i fyzický řádek (nepřípustné při disallow).
 */
function cartHasForbiddenMixedDigPhysical(lines, cfg) {
  const { hasDig, hasPhys } = summarizeClassifiedMixInCart(lines, cfg);
  return hasDig && hasPhys;
}

/**
 * Bez `itemId` nejde volat removeFromCart — modal by uživatele jen „uvařil“ bez akce / v nekonečné smyčce.
 */
function remediationHasCallableRemovals(m) {
  return m.digital.length > 0 || m.physical.length > 0;
}

/** Je zapnutý pasivní check smíchaného košíku (tj. bez právě probíhajícího přidání). */
function isMixedCartPageCheckEnabled(cfg) {
  const m = cfg.cart?.mixing;
  if (m?.disallowDigitalWithNonDigital !== true) return false;
  if (m.showChoiceModal === false) return false;
  return m.checkMixedCartOnLoad !== false;
}

/**
 * DIG + non‑DIG mix: block if košík už obsahuje opačný typ než přidávaná položka.
 */
function guardCartMixDigNonDig(cfg, ctx) {
  if (cfg.cart?.mixing?.disallowDigitalWithNonDigital !== true) {
    return { allow: true };
  }
  const dl = safeGetShoptetDataLayer();
  const cartLines = getShoptetCartLines(dl);
  if (!cartLines.length) return { allow: true };

  let hasDig = false;
  let hasNonDig = false;
  for (const item of cartLines) {
    const kind = classifyCartLineForMix(item, cfg);
    if (kind === 'digital') hasDig = true;
    else if (kind === 'physical') hasNonDig = true;
  }

  if (ctx.isDigital === true && hasNonDig) {
    return {
      allow: false,
      blockType: 'mix',
      reason:
        cfg.cart?.mixing?.messages?.digitalInPhysicalCart ||
        'V košíku je fyzické zboží; digitální klíče do stejné objednávky přidat nelze.',
    };
  }
  if (ctx.isDigital === false && hasDig) {
    return {
      allow: false,
      blockType: 'mix',
      reason:
        cfg.cart?.mixing?.messages?.physicalInDigitalCart ||
        'V košíku jsou digitální produkty; fyzické zboží do stejné objednávky přidat nelze.',
    };
  }

  if (ctx.isDigital == null && (hasDig || hasNonDig) && isDebugEnabled(cfg)) {
    log(
      cfg,
      'mix guard: unknown product type (sku missing). Mix/limit may not apply. ctx=',
      { sku: ctx.sku, productId: ctx.productId, hasDig, hasNonDig },
    );
  }

  return { allow: true };
}

/**
 * Součet kusů a hodnot jen digitálních položek + plánovaný přírustek z ctx (pokud je DIG a máme cenu/množství).
 */
function guardDigitalLimits(cfg, ctx) {
  const lim = cfg.cart?.limits?.digital;
  if (!lim) return { allow: true };

  const maxQty = lim.maxItemsTotalQty;
  const maxByCurrency = lim.maxTotalWithVat;
  if (maxQty == null && (typeof maxByCurrency !== 'object' || maxByCurrency === null)) {
    return { allow: true };
  }

  const dl = safeGetShoptetDataLayer();
  const cartLines = getShoptetCartLines(dl);
  if (!cartLines.length) return { allow: true };

  const currency = dl.currency;
  const maxValue = maxByCurrency && typeof maxByCurrency === 'object' ? maxByCurrency[currency] : undefined;

  let digQty = 0;
  let digValue = 0;
  for (const item of cartLines) {
    if (!cartItemIsDigital(item, cfg)) continue;
    const q = Number(item.quantity);
    digQty += Number.isFinite(q) ? q : 0;
    const unit = parseFloat(item.priceWithVat);
    if (!Number.isNaN(unit)) digValue += unit * (Number.isFinite(q) ? q : 0);
  }

  if (ctx.isDigital === true && ctx.amount != null && Number.isFinite(ctx.amount)) {
    digQty += ctx.amount;
    if (ctx.price != null && Number.isFinite(ctx.price)) {
      digValue += ctx.price * ctx.amount;
    }
  }

  if (maxQty != null && Number.isFinite(maxQty) && digQty > maxQty) {
    return {
      allow: false,
      blockType: 'limit',
      limitKind: 'qty',
      maxQty,
      maxValue: maxValue != null && Number.isFinite(maxValue) ? maxValue : null,
      currency: typeof currency === 'string' ? currency : null,
      digQty,
      digValue,
      reason: `Do jedné objednávky lze vložit maximálně ${maxQty} ks digitálního zboží.`,
    };
  }

  if (maxValue != null && Number.isFinite(maxValue) && digValue > maxValue) {
    return {
      allow: false,
      blockType: 'limit',
      limitKind: 'value',
      maxQty: maxQty != null && Number.isFinite(maxQty) ? maxQty : null,
      maxValue,
      currency: typeof currency === 'string' ? currency : null,
      digQty,
      digValue,
      reason: `Do jedné objednávky lze vložit digitální zboží maximálně do ${maxValue} ${currency || ''}.`,
    };
  }

  return { allow: true };
}

/**
 * Guard pipeline: return {allow:false, reason} to block the add-to-cart.
 * Keep it sync for now; we can expand to async if needed later.
 */
function runAddToCartGuards(cfg, ctx) {
  /** @type {Array<(ctx:any)=>({allow:true}|{allow:false, reason:string})>} */
  const guards = [];

  // Built-in debug switch: block everything.
  guards.push(() => {
    if (cfg.cart?.blockAddToCart === true) {
      return { allow: false, blockType: 'debug', reason: 'Blocked by config cart.blockAddToCart=true' };
    }
    return { allow: true };
  });

  guards.push(() => guardCartMixDigNonDig(cfg, ctx));
  guards.push(() => guardDigitalLimits(cfg, ctx));

  for (const guard of guards) {
    const res = guard(ctx);
    if (res && res.allow === false) return res;
  }
  return { allow: true };
}

/** ---------- Modal: DIG / fyzický výběr (náhrada za starý `fyzOrDigCheck.js`) ---------- */

/** ---------- Modal: limit digitálu (qty/value) ---------- */

function mountCartLimitModalMarkup() {
  const root = document.createElement('div');
  root.id = 'yg-cart-limit-root';
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = `
<style>
  #yg-cart-limit-root .yg-cart-limit-panel {
    position: fixed;
    inset: 0;
    z-index: 2147483646;
    display: none;
    align-items: center;
    justify-content: center;
    padding: 18px;
    font-family: inherit;
    color: #1a1a1a;
  }
  #yg-cart-limit-root.yg-cart-limit-open .yg-cart-limit-panel { display: flex; }
  #yg-cart-limit-root .yg-cart-limit-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  #yg-cart-limit-root .yg-cart-limit-dialog {
    position: relative;
    z-index: 1;
    width: min(720px, 100%);
    background: #fff;
    border-radius: 16px;
    padding: 38px 38px 34px;
    box-shadow: 0 16px 56px rgba(0, 0, 0, 0.22);
    text-align: center;
  }
  #yg-cart-limit-root .yg-cart-limit-dialog h3 {
    margin: 0 0 14px;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: normal;
  }
  #yg-cart-limit-root .yg-cart-limit-body {
    color: #677077;
    font-size: 17px;
    line-height: 1.5;
    margin: 0 0 18px;
  }
  #yg-cart-limit-root .yg-cart-limit-body p { margin: 0 0 10px; }
  #yg-cart-limit-root .yg-cart-limit-body p:last-child { margin-bottom: 0; }
  #yg-cart-limit-root .yg-cart-limit-body strong { color: #4f5558; font-weight: 600; }
  #yg-cart-limit-root .yg-cart-limit-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 18px;
  }
  #yg-cart-limit-root .yg-cart-limit-actions button {
    --yg-accent: #94d42f;
    -webkit-appearance: none;
    appearance: none;
    border: 2px solid var(--yg-accent);
    background: #fff;
    color: #1a1a1a;
    border-radius: 12px;
    padding: 14px 22px;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    min-width: 220px;
    transition: transform 0.06s ease, background 0.18s ease, color 0.18s ease, border-color 0.18s ease,
      box-shadow 0.18s ease;
  }
  #yg-cart-limit-root .yg-cart-limit-actions button:hover,
  #yg-cart-limit-root .yg-cart-limit-actions button:focus-visible {
    background: var(--yg-accent);
    border-color: var(--yg-accent);
    color: #fff;
    box-shadow: 0 8px 28px rgba(148, 212, 47, 0.42);
  }
  #yg-cart-limit-root .yg-cart-limit-actions button:active { transform: translateY(1px); }
</style>
<div class="yg-cart-limit-panel">
  <div class="yg-cart-limit-overlay" aria-hidden="true"></div>
  <div class="yg-cart-limit-dialog" role="dialog" aria-modal="true" aria-labelledby="yg-cart-limit-title">
    <h3 id="yg-cart-limit-title"></h3>
    <div class="yg-cart-limit-body"></div>
    <div class="yg-cart-limit-actions">
      <button type="button" class="yg-cart-limit-btn-ok">Rozumím</button>
    </div>
  </div>
</div>`;
  document.body.appendChild(root);
  return root;
}

function ensureCartLimitModal() {
  let root = document.getElementById('yg-cart-limit-root');
  if (!root) root = mountCartLimitModalMarkup();
  return root;
}

function openCartDigitalLimitModal(cfg, decision) {
  const root = ensureCartLimitModal();
  const titleEl = root.querySelector('#yg-cart-limit-title');
  const bodyEl = root.querySelector('.yg-cart-limit-body');
  const okBtn = root.querySelector('.yg-cart-limit-btn-ok');
  const overlay = root.querySelector('.yg-cart-limit-overlay');

  if (titleEl) titleEl.textContent = 'Limit pro digitální objednávku';

  const maxQty = decision?.maxQty;
  const maxValue = decision?.maxValue;
  const currency = decision?.currency;

  const parts = [];
  parts.push('<p><strong>Omlouváme se</strong>, ale z technických důvodů nejde do jedné objednávky vložit více digitálního zboží.</p>');
  if (maxQty != null) parts.push(`<p>Maximálně <strong>${maxQty} ks</strong> digitálních položek na objednávku.</p>`);
  if (maxValue != null) parts.push(`<p>Maximálně <strong>${maxValue} ${currency || ''}</strong> digitálního zboží na objednávku.</p>`);
  parts.push('<p>Pokud potřebujete více položek, rozdělte prosím nákup do více objednávek.</p>');

  if (bodyEl) bodyEl.innerHTML = parts.join('');

  const releaseUi = () => {
    root.classList.remove('yg-cart-limit-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  const bindOnce = (el, type, fn) => {
    if (!el || !(el instanceof HTMLElement)) return;
    const key = `ygBound${type}`;
    if (el.dataset[key] === '1') return;
    el.dataset[key] = '1';
    el.addEventListener(type, fn);
  };

  bindOnce(okBtn, 'click', () => releaseUi());
  bindOnce(overlay, 'click', () => releaseUi());

  root.classList.add('yg-cart-limit-open');
  root.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  log(cfg, 'digital limit modal opened', decision);
}

/** @param {Record<string, unknown>} cfg */
function cartMixIconUrls(cfg) {
  const m = cfg.cart?.mixing?.modal || {};
  const baseRaw = typeof m.iconBaseUrl === 'string' ? m.iconBaseUrl.trim().replace(/\/+$/, '') : '/img';
  const base = baseRaw || '/img';
  const dig = typeof m.digitalIconUrl === 'string' && m.digitalIconUrl.trim() ? m.digitalIconUrl.trim() : `${base}/key.svg`;
  const phy = typeof m.physicalIconUrl === 'string' && m.physicalIconUrl.trim() ? m.physicalIconUrl.trim() : `${base}/box.svg`;
  return { digital: dig, physical: phy };
}

/** @type {AbortController | null} */
let cartMixChoiceAbort = null;

/** @type {ReturnType<typeof setTimeout> | null} */
let passiveMixedCartTimer = null;

function mountCartMixModalMarkup() {
  const root = document.createElement('div');
  root.id = 'yg-cart-mix-root';
  root.setAttribute('aria-hidden', 'true');
  root.innerHTML = `
<style>
  #yg-cart-mix-root .yg-cart-mix-panel {
    position: fixed;
    inset: 0;
    display: none;
    align-items: center;
    justify-content: center;
    z-index: 999991;
    font-family: var(--template-font, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif);
    font-size: 15px;
    line-height: 1.45;
    color: #1a1a1a;
  }
  #yg-cart-mix-root.yg-cart-mix-open .yg-cart-mix-panel { display: flex; }
  #yg-cart-mix-root .yg-cart-mix-overlay {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  #yg-cart-mix-root .yg-cart-mix-dialog {
    position: relative;
    z-index: 1;
    box-sizing: border-box;
    max-width: 780px;
    width: calc(100% - 48px);
    background: #fff !important;
    border-radius: 12px;
    padding: 48px 52px 44px;
    text-align: center;
    border: none;
    margin: 0;
    box-shadow: 0 16px 56px rgba(0, 0, 0, 0.22);
  }
  #yg-cart-mix-root .yg-cart-mix-dialog h3 {
    margin: 0 0 22px;
    font-size: 32px;
    font-weight: 800;
    font-style: italic;
    font-synthesis: none;
    line-height: 1.18;
    color: #111;
    letter-spacing: normal;
  }
  #yg-cart-mix-root .yg-cart-mix-body {
    color: #677077;
    font-size: 18px;
    line-height: 1.55;
    max-width: 50rem;
    margin-left: auto;
    margin-right: auto;
  }
  #yg-cart-mix-root .yg-cart-mix-body p {
    margin: 0 0 12px;
  }
  #yg-cart-mix-root .yg-cart-mix-body p:last-child {
    margin-bottom: 0;
  }
  #yg-cart-mix-root .yg-cart-mix-body strong {
    color: #4f5558;
    font-weight: 600;
  }
  #yg-cart-mix-root .yg-cart-mix-actions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 32px;
    justify-content: center;
    align-items: stretch;
  }
  #yg-cart-mix-root .yg-cart-mix-actions button {
    --yg-mix-accent: #94d42f;
    -webkit-appearance: none;
    appearance: none;
    box-sizing: border-box;
    flex: 1 1 260px;
    min-width: min(100%, 260px);
    max-width: 100%;
    cursor: pointer;
    margin: 0;
    padding: 20px 36px;
    border-radius: 8px !important;
    border: 2px solid #1f1f1f !important;
    background: #fff !important;
    background-image: none !important;
    box-shadow: none !important;
    color: #1a1a1a !important;
    font-size: 16px !important;
    font-weight: 500 !important;
    font-family: inherit !important;
    line-height: 1.35 !important;
    text-align: center;
    text-decoration: none !important;
    text-shadow: none !important;
    transition: background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease,
      filter 0.18s ease;
  }
  #yg-cart-mix-root .yg-cart-mix-actions button:disabled {
    cursor: not-allowed;
    color: #8c8c8c !important;
    border-color: #d0d0d0 !important;
    border-width: 2px !important;
    background: #fff !important;
  }
  #yg-cart-mix-root .yg-cart-mix-actions button:disabled .yg-cart-mix-btn-icon img {
    opacity: 0.42;
    filter: grayscale(0.15);
  }
  #yg-cart-mix-root .yg-cart-mix-actions button:not(:disabled):hover,
  #yg-cart-mix-root .yg-cart-mix-actions button:not(:disabled):focus-visible {
    background: var(--yg-mix-accent) !important;
    border-color: var(--yg-mix-accent) !important;
    color: #fff !important;
    outline: none !important;
    box-shadow: 0 8px 28px rgba(148, 212, 47, 0.42) !important;
  }
  #yg-cart-mix-root .yg-cart-mix-btn-inner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    width: 100%;
  }
  #yg-cart-mix-root .yg-cart-mix-btn-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    line-height: 0;
  }
  #yg-cart-mix-root .yg-cart-mix-actions button:not(:disabled) .yg-cart-mix-btn-icon img {
    filter: saturate(1.15) contrast(1.02);
  }
  #yg-cart-mix-root .yg-cart-mix-btn-icon img {
    display: block;
    width: 22px;
    height: 22px;
    max-width: 22px;
    max-height: 22px;
    object-fit: contain;
    flex-shrink: 0;
  }
  #yg-cart-mix-root .yg-cart-mix-btn-label {
    flex: 0 1 auto;
    min-width: 0;
    text-align: center;
  }
  #yg-cart-mix-root .yg-cart-mix-actions button:not(:disabled):hover .yg-cart-mix-btn-icon img,
  #yg-cart-mix-root .yg-cart-mix-actions button:not(:disabled):focus-visible .yg-cart-mix-btn-icon img {
    filter: brightness(0) invert(1);
    opacity: 1;
  }
  #yg-cart-mix-root .yg-cart-mix-btn-label em {
    font-style: italic;
    font-weight: 500;
    font-synthesis: none;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 0.18em;
    text-decoration-skip-ink: auto;
  }
  #yg-cart-mix-root .yg-cart-mix-footnote {
    margin: 28px auto 0;
    padding: 0;
    font-size: 16px !important;
    line-height: 1.55;
    color: #677077 !important;
    text-align: center;
    max-width: 50rem;
  }
  #yg-cart-mix-root .yg-cart-mix-footnote strong {
    color: #4f5558 !important;
    font-weight: 600;
  }
</style>
<div class="yg-cart-mix-panel">
  <div class="yg-cart-mix-overlay" aria-hidden="true"></div>
  <div class="yg-cart-mix-dialog" role="dialog" aria-modal="true" aria-labelledby="yg-cart-mix-title">
    <h3 id="yg-cart-mix-title"></h3>
    <div class="yg-cart-mix-body"></div>
    <div class="yg-cart-mix-actions">
      <button type="button" class="yg-cart-mix-btn-digital" data-yg-mix="keep-digital">
        <span class="yg-cart-mix-btn-inner">
          <span class="yg-cart-mix-btn-icon"><img alt="" width="22" height="22" decoding="async" /></span>
          <span class="yg-cart-mix-btn-label"></span>
        </span>
      </button>
      <button type="button" class="yg-cart-mix-btn-physical" data-yg-mix="keep-physical">
        <span class="yg-cart-mix-btn-inner">
          <span class="yg-cart-mix-btn-icon"><img alt="" width="22" height="22" decoding="async" /></span>
          <span class="yg-cart-mix-btn-label"></span>
        </span>
      </button>
    </div>
    <p class="yg-cart-mix-footnote"></p>
  </div>
</div>`;

  document.body.appendChild(root);

  return root;
}

function ensureCartMixModal() {
  let root = document.getElementById('yg-cart-mix-root');
  if (root?.querySelector('.yg-cart-mix-btn-cancel')) {
    root.remove();
    root = null;
  }
  if (root) return root;
  return mountCartMixModalMarkup();
}

/** @returns {{ digital: string[], physical: string[] }} */
function collectDigitalAndPhysicalLineIds(cfg) {
  const dl = safeGetShoptetDataLayer();
  const lines = getShoptetCartLines(dl);
  /** @type {string[]} */
  const digital = [];
  /** @type {string[]} */
  const physical = [];
  for (const item of lines) {
    const id = item?.itemId;
    if (id == null || id === '') continue;
    const kind = classifyCartLineForMix(item, cfg);
    if (kind === 'digital') digital.push(String(id));
    else if (kind === 'physical') physical.push(String(id));
  }
  return { digital, physical };
}

/**
 * Před naším mix dialogem nevoláme `shoptet.modal.close()` — na některých storefrontech
 * není dostupný jQuery Colorbox (`$.colorbox`) a jejich wrapper pak vyhodí nekontrolovatelnou výjimku (kaskáda `$ is undefined`).
 * Proto zde jQuery ani nenačítáme ani nevoláme — jen čistě DOM/CSS třídy, pokud prvky na stránce jsou.
 */
function maybeDismissShoptetLightboxArtifacts() {
  try {
    const box = document.getElementById('colorbox');
    const ov = document.getElementById('cboxOverlay');
    box?.classList?.remove?.('active');
    ov?.classList?.remove?.('active');
    document.querySelector('.overall-wrapper')?.classList?.remove?.('active');
  } catch (_) {
    // ignore
  }
}

function removeCartItemsSequential(cfg, itemIds, onDone) {
  const rm = window.shoptet?.cartShared?.removeFromCart;
  const delay = typeof cfg.cart?.mixing?.betweenRemoveDelayMs === 'number' ? cfg.cart.mixing.betweenRemoveDelayMs : 140;
  const after = typeof cfg.cart?.mixing?.afterRemovalsDelayMs === 'number' ? cfg.cart.mixing.afterRemovalsDelayMs : 280;

  if (!rm || !Array.isArray(itemIds) || itemIds.length === 0) {
    setTimeout(onDone, after);
    return;
  }

  let i = 0;
  const step = () => {
    if (i >= itemIds.length) {
      setTimeout(onDone, after);
      return;
    }
    try {
      rm({ itemId: itemIds[i++] });
    } catch (e) {
      log(cfg, 'removeFromCart error', e);
      i += 1;
    }
    setTimeout(step, delay);
  };

  step();
}

/**
 * @param {Record<string, unknown>} cfg
 * @param {(() => void) | undefined} resumeAfterRemoval po úklidu znovu spustí přidání do košíku (jen flow u ajaxSubmitForm)
 * @param {{ passivePrompt?: boolean; incomingIsDigital?: boolean }} [opts]
 *   passivePrompt — smíchaný košík bez probíhajícího AJAX přidání;
 *   incomingIsDigital — blokujeme přidání digitální (true) resp. fyzické (false) položky; určuje kdy jde „ponechat ten druhý“ jen zrušit přidání.
 */
function openCartMixChoiceModal(cfg, resumeAfterRemoval, opts) {
  const passivePrompt = opts?.passivePrompt === true;
  const incomingIsDigital =
    passivePrompt ? null : typeof opts?.incomingIsDigital === 'boolean' ? opts.incomingIsDigital : null;
  const incomingBlockActive = typeof incomingIsDigital === 'boolean';
  const baskets0 = collectDigitalAndPhysicalLineIds(cfg);

  if (!remediationHasCallableRemovals(baskets0)) {
    log(
      cfg,
      'mix modal skipped — směs např. jen v datech bez itemId řádků; nelze mazat přes cartShared.removeFromCart',
    );
    if (resumeAfterRemoval && cfg.cart?.showAlertOnBlock === true && !passivePrompt) {
      window.alert(
        cfg.cart?.mixing?.messages?.remediationUnavailable ||
          'Nepovolená kombinace v košíku, ale systém nedokáže položku automaticky odebrat. Upravte košík ručně (např. v /kosik/).',
      );
    }
    return false;
  }

  const root = ensureCartMixModal();
  const m = cfg.cart?.mixing?.modal || {};

  const titleEl = root.querySelector('#yg-cart-mix-title');
  const bodyEl = root.querySelector('.yg-cart-mix-body');
  const footEl = root.querySelector('.yg-cart-mix-footnote');
  const btnDig = root.querySelector('[data-yg-mix="keep-digital"]');
  const btnFyz = root.querySelector('[data-yg-mix="keep-physical"]');

  if (!titleEl || !bodyEl || !footEl || !btnDig || !btnFyz) return false;

  cartMixChoiceAbort?.abort();
  cartMixChoiceAbort = new AbortController();
  const { signal } = cartMixChoiceAbort;

  titleEl.textContent =
    passivePrompt ? m.passiveTitle || m.title || 'Nepovolená kombinace zboží' : m.title || 'Nepovolená kombinace zboží';
  bodyEl.innerHTML = passivePrompt
    ? (m.passiveBodyHtml ??
      '<p>V košíku máte zároveň <strong>digitální klíče</strong> s <strong>fyzickým zbožím</strong>, což nelze v jedné objednávce kombinovat.</p>' +
        '<p>Tato situace může vzniknout např. při práci ve více záložkách — <strong>zvolte</strong>, který typ má z košíku zůstat; druhý odebereme.</p>')
    : m.bodyHtml ||
      '<p>Z technických důvodů není možné kombinovat <strong>digitální klíče</strong> s <strong>fyzickým zbožím</strong> v jednom košíku.</p>';

  const { digital: iconDigUrl, physical: iconPhyUrl } = cartMixIconUrls(cfg);
  const imgDigEl = btnDig.querySelector('.yg-cart-mix-btn-icon img');
  const lblDigEl = btnDig.querySelector('.yg-cart-mix-btn-label');
  const imgPhyEl = btnFyz.querySelector('.yg-cart-mix-btn-icon img');
  const lblPhyEl = btnFyz.querySelector('.yg-cart-mix-btn-label');

  if (imgDigEl) imgDigEl.setAttribute('src', iconDigUrl);
  if (imgPhyEl) imgPhyEl.setAttribute('src', iconPhyUrl);

  if (lblDigEl) {
    if (typeof m.keepDigitalLabel === 'string' && m.keepDigitalLabel.trim())
      lblDigEl.textContent = m.keepDigitalLabel;
    else lblDigEl.innerHTML = 'Ponechat <em>digitální</em> produkty';
  }
  if (lblPhyEl) {
    if (typeof m.keepPhysicalLabel === 'string' && m.keepPhysicalLabel.trim())
      lblPhyEl.textContent = m.keepPhysicalLabel;
    else lblPhyEl.innerHTML = 'Ponechat <em>fyzické</em> produkty';
  }

  if (typeof m.footnoteHtml === 'string' && m.footnoteHtml.trim()) footEl.innerHTML = m.footnoteHtml;
  else if (typeof m.footnote === 'string' && m.footnote.trim()) footEl.textContent = m.footnote;
  else {
    footEl.innerHTML =
      'Prosíme vás o vytvoření <strong>2 oddělených objednávek</strong> – jednu na digitální klíče, druhou na fyzické zboží. Omlouváme se za komplikace a děkujeme za vaši přízeň!';
  }

  maybeDismissShoptetLightboxArtifacts();

  const releaseUi = () => {
    root.classList.remove('yg-cart-mix-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  const closeSession = () => {
    cartMixChoiceAbort?.abort();
    cartMixChoiceAbort = null;
    releaseUi();
  };

  const enableButtons = () => {
    btnDig.disabled = btnFyz.disabled = false;
  };

  const refreshRemediationBuckets = () => collectDigitalAndPhysicalLineIds(cfg);

  /**
   * Směs v košíku už není → zavřít (např. upravil jiný tab).
   * Při blokovaném přidání zatím košík „smíchaný“ není → nikdy nevolat při prvním kliknutí (`incomingBlockActive`).
   */
  const closeIfMixedAlreadyGoneThenMaybeResume = () => {
    if (incomingBlockActive) return false;
    const linesNow = getShoptetCartLines(safeGetShoptetDataLayer());
    if (!cartHasForbiddenMixedDigPhysical(linesNow, cfg)) {
      closeSession();
      enableButtons();
      if (resumeAfterRemoval) {
        try {
          resumeAfterRemoval();
        } catch (e) {
          log(cfg, 'resumeAfterRemoval after mix self-resolved failed', e);
        }
      }
      return true;
    }
    return false;
  };

  /** Blokovat Escape (dialog musí rozřešit jedním ze dvou tlačítek). */
  /** @param {KeyboardEvent} e */
  const onKeyTrap = e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  btnDig.disabled =
    baskets0.physical.length === 0 && !(incomingBlockActive === true && incomingIsDigital === false);
  btnFyz.disabled =
    baskets0.digital.length === 0 && !(incomingBlockActive === true && incomingIsDigital === true);

  root.classList.add('yg-cart-mix-open');
  root.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';

  document.addEventListener('keydown', onKeyTrap, { signal, capture: true });

  const staleExitRemediationModal = reason => {
    log(cfg, 'mix remediation modal closed (edge case)', reason);
    closeSession();
    enableButtons();
  };

  const afterCleanup = () => {
    closeSession();
    enableButtons();
    if (resumeAfterRemoval) {
      try {
        resumeAfterRemoval();
      } catch (e) {
        log(cfg, 'resumeAfterRemoval after cart mix failed', e);
      }
    }
    // při výběru v pasivním modalu počkáme na Shoptet (`ShoptetCartUpdated` apod.)
  };

  btnDig.onclick = () => {
    if (closeIfMixedAlreadyGoneThenMaybeResume()) return;
    const b = refreshRemediationBuckets();
    if (!b.physical.length) {
      if (incomingBlockActive && incomingIsDigital === false) {
        log(cfg, 'cart mix choice: keep digital (= zrušit přidávání fyzického)');
        closeSession();
        enableButtons();
        return;
      }
      if (!closeIfMixedAlreadyGoneThenMaybeResume())
        staleExitRemediationModal({ expectedPhysicalIds: false });
      return;
    }
    btnDig.disabled = btnFyz.disabled = true;
    log(cfg, 'cart mix choice: keep digital → remove physical lines', b.physical);
    removeCartItemsSequential(cfg, b.physical, afterCleanup);
  };

  btnFyz.onclick = () => {
    if (closeIfMixedAlreadyGoneThenMaybeResume()) return;
    const b = refreshRemediationBuckets();
    if (!b.digital.length) {
      if (incomingBlockActive && incomingIsDigital === true) {
        log(cfg, 'cart mix choice: keep physical (= zrušit přidávání digitálního)');
        closeSession();
        enableButtons();
        return;
      }
      if (!closeIfMixedAlreadyGoneThenMaybeResume())
        staleExitRemediationModal({ expectedDigitalIds: false });
      return;
    }
    btnDig.disabled = btnFyz.disabled = true;
    log(cfg, 'cart mix choice: keep physical → remove digital lines', b.digital);
    removeCartItemsSequential(cfg, b.digital, afterCleanup);
  };

  return true;
}

function runPassiveMixedCartCheck(cfg) {
  if (!isMixedCartPageCheckEnabled(cfg)) return;
  const dl = safeGetShoptetDataLayer();
  const lines = getShoptetCartLines(dl);
  if (!lines.length || !cartHasForbiddenMixedDigPhysical(lines, cfg)) return;

  const buckets = collectDigitalAndPhysicalLineIds(cfg);
  if (!remediationHasCallableRemovals(buckets)) {
    log(cfg, 'passive mixed skipped — řádky bez itemId nelze mazat rozhraním košíku');
    return;
  }

  const rootEl = document.getElementById('yg-cart-mix-root');
  if (rootEl?.classList.contains('yg-cart-mix-open')) return;

  log(cfg, 'passive mixed cart — opening remediation modal');
  openCartMixChoiceModal(cfg, undefined, { passivePrompt: true });
}

function schedulePassiveMixedCartCheck(cfg) {
  if (!isMixedCartPageCheckEnabled(cfg)) return;
  const delay =
    typeof cfg.cart?.mixing?.mixedCartCheckDebounceMs === 'number'
      ? cfg.cart.mixing.mixedCartCheckDebounceMs
      : 400;
  if (passiveMixedCartTimer != null) clearTimeout(passiveMixedCartTimer);
  passiveMixedCartTimer = setTimeout(() => {
    passiveMixedCartTimer = null;
    runPassiveMixedCartCheck(cfg);
  }, delay);
}

function installPassiveMixedCartCheck(cfg) {
  if (!isMixedCartPageCheckEnabled(cfg)) return;
  /** @type {string[]} */
  const evts = [
    'ShoptetDOMContentLoaded',
    'ShoptetDOMCartContentLoaded',
    'ShoptetDOMCartCountUpdated',
    'ShoptetCartUpdated',
  ];
  evts.forEach(evt => document.addEventListener(evt, () => schedulePassiveMixedCartCheck(cfg)));

  schedulePassiveMixedCartCheck(cfg);

  window.addEventListener('pageshow', e => {
    if (/** @type {PageTransitionEvent} */ (e).persisted) schedulePassiveMixedCartCheck(cfg);
  });
}

/**
 * Basic event taps you can use to verify what's happening.
 */
function installEventMonitoring(cfg) {
  const events = [
    // DOM lifecycle
    'ShoptetDOMContentLoaded',
    'ShoptetDOMCartContentLoaded',
    'ShoptetDOMCartCountUpdated',

    // cart custom events
    'ShoptetCartAddCartItem',
    'ShoptetCartAddCartItemFailed',
    'ShoptetCartUpdated',
  ];

  events.forEach(evt => {
    document.addEventListener(evt, e => {
      log(cfg, 'event', evt, e?.detail ?? null);
    });
  });
}

/**
 * Hook a monitored function by wrapping it.
 *
 * Why wrapper (not only dev-tools events):
 * - Dev-tools fires events AFTER a function runs.
 * - For "prevent" we need a BEFORE hook (this wrapper is "before").
 *
 * Returning false from addToCart is not guaranteed to stop Shoptet flow,
 * so the initial version only logs + provides a single decision point.
 */
function wrapMonitoredFunction(cfg, path, wrapperFactory) {
  const [ns, mod, fn] = path.split('.');
  const root = window?.[ns]?.[mod];
  if (!root || typeof root[fn] !== 'function') return false;

  const original = root[fn].bind(root);
  root[fn] = wrapperFactory(original);
  log(cfg, 'wrapped', path);
  return true;
}

function installAddToCartHook(cfg) {
  // Most relevant monitored function for adding to cart
  const ok1 = wrapMonitoredFunction(cfg, 'shoptet.cartShared.addToCart', original => {
    return function wrappedAddToCart(...args) {
      log(cfg, 'before shoptet.cartShared.addToCart', { args });

      // TODO: here we will implement rules:
      // - prevent mixing DIG with others
      // - limit DIG count/value
      //
      // For now we just allow the original call.
      return original(...args);
    };
  });

  // In many templates, add-to-cart goes through ajaxSubmitForm with /action/Cart/addCartItem/
  const ok2 = wrapMonitoredFunction(cfg, 'shoptet.cart.ajaxSubmitForm', original => {
    return function wrappedAjaxSubmitForm(...args) {
      const [url] = args;
      const isAddCartItem =
        typeof url === 'string' && url.toLowerCase().includes('/action/cart/addcartitem/');

      if (isAddCartItem) {
        const form = args[1];
        const ctx = buildAddToCartContext({ url, form, rawArgs: args });
        log(cfg, 'before shoptet.cart.ajaxSubmitForm (addCartItem)', ctx);

        const decision = runAddToCartGuards(cfg, ctx);
        if (decision.allow === false) {
          log(cfg, 'BLOCKED add-to-cart', { reason: decision.reason, ctx, blockType: decision.blockType });

          const showMix =
            decision.blockType === 'mix' && cfg.cart?.mixing?.showChoiceModal !== false;

          if (showMix) {
            openCartMixChoiceModal(cfg, () => original(...args), {
              incomingIsDigital: ctx.isDigital === true ? true : ctx.isDigital === false ? false : undefined,
            });
            return false;
          }

          const showLimit = decision.blockType === 'limit';
          if (showLimit) {
            openCartDigitalLimitModal(cfg, decision);
            return false;
          }

          if (cfg.cart?.showAlertOnBlock === true && typeof decision.reason === 'string') {
            window.alert(decision.reason);
          }
          return false;
        }
      }

      return original(...args);
    };
  });

  if (!ok1 && !ok2) {
    log(cfg, 'add-to-cart functions not found yet (will exist after Shoptet loads scripts)');
  }
}

function init() {
  const cfg = getConfig();

  // Convenience helpers for copying logs from console
  window.YG = window.YG || {};
  window.YG.dumpLog = () => JSON.parse(JSON.stringify(window[LOG_KEY] || []));
  window.YG.clearLog = () => {
    window[LOG_KEY] = [];
  };

  installEventMonitoring(cfg);
  installPassiveMixedCartCheck(cfg);
  installMenuCatalog(cfg, log);
  installCheckoutBillingShipping(getConfig);
  installCheckoutCustomerDetails(getConfig);

  // Try immediately…
  installAddToCartHook(cfg);
  // …and also after Shoptet AJAX DOM updates (scripts might appear late on some pages).
  document.addEventListener('ShoptetDOMContentLoaded', () => {
    installAddToCartHook(cfg);
    schedulePassiveMixedCartCheck(cfg);
  });
}

init();